import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Row, Col, Container, Card } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  createProduct,
  updateProduct,
  fetchProductById,
  clearSuccess,
  setCurrentProduct,
} from "../../redux/slices/productSlice";
import { fetchCategories } from "../../redux/slices/categorySlice";
import subCategoryService from "../../services/subCategoryService";
import categoryService from "../../services/categoryService";
import { toast } from "react-toastify";
import { FaRegTrashAlt } from "react-icons/fa";
import boardService from "../../services/boardService";
import classService from "../../services/classService";
import subjectService from "../../services/subjectService";
import collegeService from "../../services/collegeService";
import examMasterService from "../../services/examMasterService";
import userAdminService from "../../services/userAdminService";

const ProductForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEditMode = !!id;

  const { isCreating, isUpdating, isLoading, error, success, currentProduct } =
    useSelector((state) => state.products);

  const { user } = useSelector((state) => state.auth);

  const { categories = [] } = useSelector((state) => state.categories);

  const [vendors, setVendors] = useState([]);

  const [formData, setFormData] = useState({
    product_name: "",
    product_sku: "",
    product_description: "",
    brand_publisher: "",
    book_language: "English",
    mrp: 0.0,
    selling_price: 0.0,
    discount_percentage: 0,
    gst_percentage: 0,
    stock_quantity: 0,
    low_stock_alert: 5,
    category_id: "",
    sub_category_id: "",
    product_status: "UNLISTED",
    image_url: "",
    board_id: "",
    class_id: "",
    subject_id: "",
    university_id: "",
    course_id: "",
    semester_id: "",
    exam_category_id: "",
    exam_id: "",
    competitive_subject_id: "",
    color: "",
    size: "",
    material: "",
    weight: "",
    capacity: "",
    gender: "",
    age_group: "",
    battery_required: false,
    medium: "",
    author_name: "",
    edition: "",
    isbn: "",
    barcode: "",
    preview_pdf: "",
    regulation_year: "",
    vendor_id: "",
    product_price: 0.0,
    gst_amount: 0.0,
    commission_type: "percentage",
    commission_value: 0.0,
    commission_amount: 0.0,
  });

  const [flowType, setFlowType] = useState("normal");
  const [activeCategories, setActiveCategories] = useState([]);
  const [boards, setBoards] = useState([]);
  const [classes, setClasses] = useState([]);
  const [academicSubjects, setAcademicSubjects] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);

  // Competitive Exam State
  const [examCategories, setExamCategories] = useState([]);
  const [exams, setExams] = useState([]);
  const [competitiveSubjects, setCompetitiveSubjects] = useState([]);

  const [isLoadingAcademic, setIsLoadingAcademic] = useState(false);

  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageUrls, setRemovedImageUrls] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [isLoadingSubCats, setIsLoadingSubCats] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false); // Added isUploading state

  // Load product data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchProductById(id));
    }
  }, [dispatch, id, isEditMode]);

  // Load categories
  useEffect(() => {
    dispatch(fetchCategories());
    // Added by Farhan Khan on 22-June-2026 ( Using this to Fetch Active Data for dropdown )
    categoryService.getActiveCategories().then((res) => {
      const cats = res.categories || res.data?.categories || res || [];
      setActiveCategories(cats);
    });
  }, [dispatch]);

  // Load vendors for admin
  // Added by Farhan Khan on 22-June-2026 ( Using this to Fetch Active Data for dropdown )
  useEffect(() => {
    if (user?.user_type === "admin" || user?.user_type === "super_admin") {
      userAdminService.getActiveVendors().then((res) => {
        const activeVends = res.data || res || [];
        setVendors(activeVends);
      });
    }
  }, [user]);



  // Update form data when currentProduct changes
  useEffect(() => {
    if (isEditMode && currentProduct) {
      setFormData({
        product_name: currentProduct.product_name || "",
        product_sku: currentProduct.product_sku || "",
        product_description: currentProduct.product_description || "",
        brand_publisher: currentProduct.brand_publisher || "",
        book_language: currentProduct.book_language || "English",
        mrp: currentProduct.mrp || 0.0,
        selling_price: currentProduct.selling_price || 0.0,
        discount_percentage: currentProduct.discount_percentage || 0,
        gst_percentage: currentProduct.gst_percentage || 0,
        stock_quantity: currentProduct.stock_quantity || 0,
        low_stock_alert: currentProduct.low_stock_alert || 5,
        category_id: currentProduct.category_id || "",
        sub_category_id: currentProduct.sub_category_id || "",
        product_status: currentProduct.product_status || "UNLISTED",
        image_url: currentProduct.image_url || "",
        board_id: currentProduct.board_id || "",
        class_id: currentProduct.class_id || "",
        subject_id: currentProduct.subject_id || "",
        university_id: currentProduct.university_id || "",
        course_id: currentProduct.course_id || "",
        semester_id: currentProduct.semester_id || "",
        exam_category_id: currentProduct.exam_category_id || "",
        exam_id: currentProduct.exam_id || "",
        competitive_subject_id: currentProduct.competitive_subject_id || "",
        color: currentProduct.color || "",
        size: currentProduct.size || "",
        material: currentProduct.material || "",
        weight: currentProduct.weight || "",
        capacity: currentProduct.capacity || "",
        gender: currentProduct.gender || "",
        age_group: currentProduct.age_group || "",
        battery_required: currentProduct.battery_required || false,
        medium: currentProduct.medium || "",
        author_name: currentProduct.author_name || "",
        edition: currentProduct.edition || "",
        isbn: currentProduct.isbn || "",
        barcode: currentProduct.barcode || "",
        preview_pdf: currentProduct.preview_pdf || "",
        regulation_year: currentProduct.regulation_year || "",
        vendor_id: currentProduct.vendor_id || currentProduct.createdbyid || "",
        product_price: currentProduct.product_price !== undefined ? currentProduct.product_price : 0.0,
        gst_amount: currentProduct.gst_amount !== undefined ? currentProduct.gst_amount : 0.0,
        commission_type: currentProduct.commission_type || "percentage",
        commission_value: currentProduct.commission_value !== undefined ? currentProduct.commission_value : 0.0,
        commission_amount: currentProduct.commission_amount !== undefined ? currentProduct.commission_amount : 0.0,
      });

      if (currentProduct.category_id) {
        loadSubCategories(currentProduct.category_id);
        const selectedCat = categories.find(c => c.id === currentProduct.category_id);
        if (selectedCat) {
          setFlowType(selectedCat.flow_type || "normal");
          handleAcademicInitialLoad(selectedCat.flow_type, currentProduct);
        }
      }



      // Set existing images for edit mode - handle both image_url array and parsed image_url
      if (currentProduct) {
        if (
          currentProduct.image_url &&
          Array.isArray(currentProduct.image_url)
        ) {
          // Use image_url array directly
          setExistingImages(currentProduct.image_url);
        } else if (currentProduct.image_url) {
          // Parse image_url if it's a JSON string
          const parsed = parseExistingImages(currentProduct.image_url);
          setExistingImages(parsed);
        } else {
          setExistingImages([]);
        }
      }
    }
  }, [currentProduct, isEditMode]);

  // Cleanup effect to clear state when component unmounts
  useEffect(() => {
    return () => {
      setImageFiles([]);
      setExistingImages([]);
      setRemovedImageUrls([]);
    };
  }, []);

  // Pricing & GST & Commission math calculations
  useEffect(() => {
    const productPrice = parseFloat(formData.product_price || 0);
    const gstPercent = parseFloat(formData.gst_percentage || 0);
    const commType = formData.commission_type || "percentage";
    const commValue = parseFloat(formData.commission_value || 0);
    const mrp = parseFloat(formData.mrp || 0);

    const gstAmount = parseFloat((productPrice * (gstPercent / 100)).toFixed(2));
    const priceWithGst = parseFloat((productPrice + gstAmount).toFixed(2));

    let commissionAmount = 0;
    if (commType === "fixed") {
      commissionAmount = parseFloat(commValue.toFixed(2));
    } else {
      commissionAmount = parseFloat((priceWithGst * (commValue / 100)).toFixed(2));
    }

    const sellingPrice = parseFloat((priceWithGst + commissionAmount).toFixed(2));

    // Calculate discount percentage
    let discountPercent = 0;
    if (mrp > 0 && sellingPrice <= mrp) {
      discountPercent = parseFloat((((mrp - sellingPrice) / mrp) * 100).toFixed(2));
    }

    setFormData((prev) => {
      const isEditingDiscount = document.activeElement && document.activeElement.name === "discount_percentage";
      
      const nextData = { ...prev };
      let changed = false;

      if (prev.gst_amount !== gstAmount) {
        nextData.gst_amount = gstAmount;
        changed = true;
      }
      if (prev.commission_amount !== commissionAmount) {
        nextData.commission_amount = commissionAmount;
        changed = true;
      }
      if (prev.selling_price !== sellingPrice) {
        nextData.selling_price = sellingPrice;
        changed = true;
      }
      if (!isEditingDiscount && prev.discount_percentage !== discountPercent) {
        nextData.discount_percentage = discountPercent;
        changed = true;
      }

      return changed ? nextData : prev;
    });
  }, [
    formData.product_price,
    formData.gst_percentage,
    formData.commission_type,
    formData.commission_value,
    formData.mrp,
  ]);

  // Handle toast messages
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
        navigate("/product");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate, dispatch]);

  // Cleanup effect to clear success message when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearSuccess());
    };
  }, [dispatch]);

  // Added by Farhan Khan on 22-June-2026 ( Using this to Fetch Active Data for dropdown )
  const loadSubCategories = async (categoryId) => {
    try {
      setIsLoadingSubCats(true);
      const resp = await subCategoryService.getActiveSubCategoriesByCategoryId(
        categoryId
      );
      setSubCategories(resp.subCategories || []);
    } catch (error) {
      setSubCategories([]);
    } finally {
      setIsLoadingSubCats(false);
    }
  };

  // Added by Farhan Khan on 22-June-2026 ( Using this to Fetch Active Data for dropdown )
  const handleAcademicInitialLoad = async (type, product) => {
    setIsLoadingAcademic(true);
    try {
      if (type === "school") {
        const boardData = await boardService.getAllBoards();
        setBoards(boardData.boards || boardData);
        if (product.board_id) {
          const classData = await classService.getAllClasses({ board_id: product.board_id });
          setClasses(classData.classes || classData);
        }
        if (product.class_id) {
          const subjectData = await subjectService.getActiveSubjectsByClass(product.class_id);
          setAcademicSubjects(subjectData.subjects || subjectData);
        }
      } else if (type === "competitive") {
        const catData = await examMasterService.getExamCategories();
        setExamCategories(catData);
        if (product.exam_category_id) {
          const examData = await examMasterService.getExams(product.exam_category_id);
          setExams(examData);
        }
        if (product.exam_id) {
          const subjectData = await examMasterService.getCompetitiveSubjects(product.exam_id);
          setCompetitiveSubjects(subjectData);
        }
      } else if (type === "college") {
        const univData = await collegeService.getActiveUniversities();
        setUniversities(univData);
        if (product.university_id) {
          const courseData = await collegeService.getActiveCourses(product.university_id);
          setCourses(courseData);
        }
        if (product.course_id) {
          const semData = await collegeService.getActiveSemesters(product.course_id);
          setSemesters(semData);
        }
        if (product.semester_id) {
          const subjectData = await subjectService.getActiveSubjectsBySemester(product.semester_id);
          setAcademicSubjects(subjectData.subjects || subjectData);
        }
      }
    } catch (error) {
      console.error("Error loading academic data:", error);
    } finally {
      setIsLoadingAcademic(false);
    }
  };

  const handleBoardChange = async (boardId) => {
    setFormData(prev => ({ ...prev, board_id: boardId, class_id: "", subject_id: "" }));
    setClasses([]);
    setAcademicSubjects([]);
    if (boardId) {
      try {
        const classData = await classService.getAllClasses({ board_id: boardId });
        setClasses(classData.classes || classData);
      } catch (error) {
        console.error("Error loading classes:", error);
      }
    }
  };

  // Added by Farhan Khan on 22-June-2026 ( Using this to Fetch Active Data for dropdown )
  const handleClassChange = async (classId) => {
    setFormData(prev => ({ ...prev, class_id: classId, subject_id: "" }));
    setAcademicSubjects([]);
    if (classId) {
      try {
        const subjectData = await subjectService.getActiveSubjectsByClass(classId);
        setAcademicSubjects(subjectData.subjects || subjectData);
      } catch (error) {
        console.error("Error loading subjects:", error);
      }
    }
  };

  // Added by Farhan Khan on 22-June-2026 ( Using this to Fetch Active Data for dropdown )
  const handleUniversityChange = async (univId) => {
    console.log("University selected:", univId);
    setFormData(prev => ({ ...prev, university_id: univId, course_id: "", semester_id: "", subject_id: "" }));
    setAcademicSubjects([]);
    setSemesters([]);
    setCourses([]);
    if (univId) {
      try {
        const courseData = await collegeService.getActiveCourses(univId);
        setCourses(courseData);
      } catch (error) {
        console.error("Error loading courses:", error);
        toast.error("Failed to load courses");
      }
    }
  };

  // Added by Farhan Khan on 22-June-2026 ( Using this to Fetch Active Data for dropdown )
  const handleCourseChange = async (courseId) => {
    console.log("Course selected:", courseId);
    setFormData(prev => ({ ...prev, course_id: courseId, semester_id: "", subject_id: "" }));
    setAcademicSubjects([]);
    setSemesters([]);
    if (courseId) {
      try {
        const semData = await collegeService.getActiveSemesters(courseId);
        setSemesters(semData);
      } catch (error) {
        console.error("Error loading semesters:", error);
        toast.error("Failed to load semesters");
      }
    }
  };

  // Added by Farhan Khan on 22-June-2026 ( Using this to Fetch Active Data for dropdown )
  const handleSemesterChange = async (semesterId) => {
    console.log("Semester selected:", semesterId);
    setFormData(prev => ({ ...prev, semester_id: semesterId, subject_id: "" }));
    setAcademicSubjects([]);
    if (semesterId) {
      try {
        const subjectData = await subjectService.getActiveSubjectsBySemester(semesterId);
        setAcademicSubjects(subjectData.subjects || subjectData);
      } catch (error) {
        console.error("Error loading subjects:", error);
        toast.error("Failed to load subjects");
      }
    }
  };

  const handleExamCategoryChange = async (examCatId) => {
    setFormData(prev => ({ ...prev, exam_category_id: examCatId, exam_id: "", competitive_subject_id: "" }));
    setExams([]);
    setCompetitiveSubjects([]);
    if (examCatId) {
      try {
        const examData = await examMasterService.getExams(examCatId);
        setExams(examData);
      } catch (error) {
        console.error("Error loading exams:", error);
      }
    }
  };

  const handleExamChange = async (examId) => {
    setFormData(prev => ({ ...prev, exam_id: examId, competitive_subject_id: "" }));
    setCompetitiveSubjects([]);
    if (examId) {
      try {
        const subjectData = await examMasterService.getCompetitiveSubjects(examId);
        setCompetitiveSubjects(subjectData);
      } catch (error) {
        console.error("Error loading competitive subjects:", error);
      }
    }
  };


  // Auto-generate product name based on academic selections
  useEffect(() => {
    // Only auto-generate for school/college flows if not in edit mode initially
    // or if the user is actively changing dropdowns
    if (flowType === "school" || flowType === "college" || flowType === "competitive") {
      let parts = [];

      if (flowType === "school") {
        if (formData.board_id) {
          const board = boards.find(b => b.id === formData.board_id);
          if (board) parts.push(board.board_name);
        }
        if (formData.class_id) {
          const cls = classes.find(c => c.id === formData.class_id);
          if (cls) parts.push(cls.class_name);
        }
        if (formData.subject_id) {
          const sub = academicSubjects.find(s => s.id === formData.subject_id);
          if (sub) parts.push(sub.subject_name);
        }
      } else if (flowType === "college") {
        if (formData.university_id) {
          const univ = universities.find(u => u.id === formData.university_id);
          if (univ) parts.push(univ.university_name);
        }
        if (formData.course_id) {
          const course = courses.find(c => c.id === formData.course_id);
          if (course) parts.push(course.course_name);
        }
        if (formData.semester_id) {
          const sem = semesters.find(s => s.id === formData.semester_id);
          if (sem) parts.push(sem.semester_name);
        }
        if (formData.subject_id) {
          const sub = academicSubjects.find(s => s.id === formData.subject_id);
          if (sub) parts.push(sub.subject_name);
        }
      } else if (flowType === "competitive") {
        if (formData.exam_category_id) {
          const cat = examCategories.find(c => c.id === formData.exam_category_id);
          if (cat) parts.push(cat.name);
        }
        if (formData.exam_id) {
          const exm = exams.find(e => e.id === formData.exam_id);
          if (exm) parts.push(exm.exam_name);
        }
        if (formData.competitive_subject_id) {
          const sub = competitiveSubjects.find(s => s.id === formData.competitive_subject_id);
          if (sub) parts.push(sub.subject_name);
        }
      }

      if (parts.length > 0) {
        const generatedName = parts.join(" - ");
        // We only auto-update if the current name is empty or looks like a generated name
        // However, user specifically asked to show combined value, so we'll set it.
        // They can still edit it afterwards.
        setFormData(prev => ({ ...prev, product_name: generatedName }));
      }
    }
  }, [
    flowType,
    formData.board_id,
    formData.class_id,
    formData.subject_id,
    formData.university_id,
    formData.course_id,
    formData.semester_id,
    formData.exam_category_id,
    formData.exam_id,
    formData.competitive_subject_id,
    boards,
    classes,
    academicSubjects,
    universities,
    courses,
    semesters,
    examCategories,
    exams,
    competitiveSubjects
  ]);

  const handleInputChange = (e) => {
    let { name, value } = e.target;

    // Prevent negative numbers for stock_quantity and low_stock_alert
    if ((name === "stock_quantity" || name === "low_stock_alert") && value !== "") {
      const num = parseInt(value, 10);
      if (!isNaN(num) && num < 0) {
        value = "0";
      }
    }

    // Clear validation error for this field when user types
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: value,
      };

      // Reset subcategory and academic fields when category changes
      if (name === "category_id") {
        const selectedCat = categories.find((c) => c.id === value);
        const type = selectedCat?.flow_type || "normal";

        setFlowType(type);
        updatedData.gst_percentage = Number(selectedCat?.gst_percentage || 0);
        updatedData.commission_type = selectedCat?.commission_type || "percentage";
        updatedData.commission_value = Number(selectedCat?.commission_value || 0);
        updatedData.sub_category_id = "";
        updatedData.board_id = "";
        updatedData.class_id = "";
        updatedData.subject_id = "";
        updatedData.university_id = "";
        updatedData.course_id = "";
        updatedData.semester_id = "";

        // Reset all academic states
        setBoards([]);
        setClasses([]);
        setAcademicSubjects([]);
        setUniversities([]);
        setCourses([]);
        setSemesters([]);

        if (value) {
          loadSubCategories(value);
          // Initial load for new flow
          if (type === "school") {
            boardService.getAllBoards().then(data => setBoards(data.boards || data));
          } else if (type === "college") {
            // Added by Farhan Khan on 22-June-2026 ( Using this to Fetch Active Data for dropdown )
            collegeService.getActiveUniversities().then(data => setUniversities(data));
          } else if (type === "competitive") {
            examMasterService.getExamCategories().then(data => setExamCategories(data));
          }
        } else {
          setSubCategories([]);
        }
      }

      // Real-time price validation
      if (name === "product_price" || name === "mrp") {
        const mrp = name === "mrp" ? parseFloat(value) : parseFloat(prev.mrp);
        const productPrice = name === "product_price" ? parseFloat(value) : parseFloat(prev.product_price);

        const gstPercent = parseFloat(prev.gst_percentage || 0);
        const commType = prev.commission_type || "percentage";
        const commValue = parseFloat(prev.commission_value || 0);

        const gstAmount = parseFloat((productPrice * (gstPercent / 100)).toFixed(2));
        const priceWithGst = parseFloat((productPrice + gstAmount).toFixed(2));

        let commissionAmount = 0;
        if (commType === "fixed") {
          commissionAmount = parseFloat(commValue.toFixed(2));
        } else {
          commissionAmount = parseFloat((priceWithGst * (commValue / 100)).toFixed(2));
        }

        const calculatedSellingPrice = parseFloat((priceWithGst + commissionAmount).toFixed(2));

        if (!isNaN(calculatedSellingPrice) && !isNaN(mrp) && calculatedSellingPrice > mrp) {
          setValidationErrors(prevErrors => ({
            ...prevErrors,
            selling_price: "Selling price (Product Price + Tax + Fee) cannot be greater than MRP"
          }));
        } else {
          setValidationErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            delete newErrors.selling_price;
            return newErrors;
          });
        }
      }

      if (name === "discount_percentage") {
        const discountPercent = value === "" ? 0 : (parseFloat(value) || 0);
        const mrp = parseFloat(prev.mrp || 0);

        if (mrp > 0) {
          const targetSellingPrice = parseFloat((mrp * (1 - discountPercent / 100)).toFixed(2));

          const gstPercent = parseFloat(prev.gst_percentage || 0);
          const commType = prev.commission_type || "percentage";
          const commValue = parseFloat(prev.commission_value || 0);

          let priceWithGst = 0;
          if (commType === "fixed") {
            priceWithGst = parseFloat((targetSellingPrice - commValue).toFixed(2));
          } else {
            priceWithGst = parseFloat((targetSellingPrice / (1 + commValue / 100)).toFixed(2));
          }

          const calculatedProductPrice = parseFloat((priceWithGst / (1 + gstPercent / 100)).toFixed(2));
          const gstAmount = parseFloat((calculatedProductPrice * (gstPercent / 100)).toFixed(2));
          const commissionAmount = parseFloat((targetSellingPrice - priceWithGst).toFixed(2));

          updatedData.product_price = !isNaN(calculatedProductPrice) && calculatedProductPrice >= 0 ? calculatedProductPrice : 0;
          updatedData.gst_amount = !isNaN(gstAmount) && gstAmount >= 0 ? gstAmount : 0;
          updatedData.commission_amount = !isNaN(commissionAmount) && commissionAmount >= 0 ? commissionAmount : 0;
          updatedData.selling_price = targetSellingPrice;
        }

        setValidationErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors.selling_price;
          return newErrors;
        });
      }

      // College Name Generation
      if (flowType === "college" && updatedData.university_id && updatedData.course_id && updatedData.subject_id && !updatedData.product_name) {
        const univ = universities.find(u => u.id === updatedData.university_id);
        const course = courses.find(c => c.id === updatedData.course_id);
        const sem = semesters.find(s => s.id === updatedData.semester_id);
        const sub = academicSubjects.find(s => s.id === updatedData.subject_id);
        
        if (univ && course && sub) {
          let generatedName = `${univ.university_name} - ${course.course_name}`;
          if (sem) generatedName += ` - ${sem.semester_name}`;
          generatedName += ` - ${sub.subject_name}`;
          updatedData.product_name = generatedName;
        }
      }

      return updatedData;
    });

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Multiple file selection handler
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      files.forEach((file) => {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} is not a valid image file`);
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is too large. Maximum size is 5MB`);
          return;
        }

        // Mandatory Dimension Validation: 2200 x 2000 Px
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          if (img.width !== 2200 || img.height !== 2000) {
            toast.error(`File upload failed: ${file.name} must be exactly 2200 x 2000 pixels. (Detected: ${img.width}x${img.height})`);
          } else {
            setImageFiles((prev) => [...prev, file]);
          }
          URL.revokeObjectURL(objectUrl);
        };
        img.onerror = () => {
          toast.error(`Failed to load image: ${file.name}`);
          URL.revokeObjectURL(objectUrl);
        };
        img.src = objectUrl;
      });
    }
    // Reset target value so same file can be selected again if removed
    e.target.value = "";
  };

  // Remove specific file handler
  const handleRemoveImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Remove all files handler
  const handleRemoveAllImages = () => {
    setImageFiles([]);
  };

  // Remove individual existing image handler
  const handleRemoveExistingImage = (index) => {
    const imageToRemove = existingImages[index];
    // Add to removed images list
    setRemovedImageUrls((prev) => [...prev, imageToRemove]);
    // Remove from existing images display
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Remove all existing images handler
  const handleRemoveAllExistingImages = () => {
    // Add all existing images to removed list
    setRemovedImageUrls((prev) => [...prev, ...existingImages]);
    // Clear existing images display
    setExistingImages([]);
  };



  // Helper function to parse existing images (now handles both old and new format)
  const parseExistingImages = (imageData) => {
    if (!imageData) return [];

    // If it's already an array (new format), return it directly
    if (Array.isArray(imageData)) {
      return imageData;
    }

    // If it's a string (old format), try to parse it
    try {
      const parsed = JSON.parse(imageData);
      return Array.isArray(parsed) ? parsed : [imageData];
    } catch (error) {
      // If JSON parsing fails, check if it's a comma-separated string
      if (imageData.includes(",")) {
        return imageData.split(",").map((url) => url.trim());
      }
      return [imageData];
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.product_name || !formData.product_name.trim()) {
      errors.product_name = "Product name is required";
    }

    if (!formData.mrp || parseFloat(formData.mrp) <= 0) {
      errors.mrp = "Valid MRP is required";
    }

    if (!formData.product_price || parseFloat(formData.product_price) <= 0) {
      errors.product_price = "Valid product price is required";
    }

    if (!formData.selling_price || parseFloat(formData.selling_price) <= 0) {
      errors.selling_price = "Valid selling price is required";
    } else if (parseFloat(formData.selling_price) > parseFloat(formData.mrp || 0)) {
      errors.selling_price = "Selling price (Product Price + Tax + Fee) cannot be greater than MRP";
    }

    if (!formData.category_id) {
      errors.category_id = "Category is required";
    }

    if (formData.category_id && subCategories.length > 0 && !formData.sub_category_id) {
      errors.sub_category_id = "Subcategory is required";
    }

    if ((user?.user_type === "admin" || user?.user_type === "super_admin") && !formData.vendor_id) {
      errors.vendor_id = "Vendor is required";
    }

    // Academic flow validations
    if (flowType === "school") {
      if (!formData.board_id) errors.board_id = "Board is required";
      if (!formData.class_id) errors.class_id = "Class is required";
      if (!formData.subject_id) errors.subject_id = "Subject is required";
    } else if (flowType === "college") {
      if (!formData.university_id) errors.university_id = "University is required";
      if (!formData.course_id) errors.course_id = "Course is required";
      if (!formData.semester_id) errors.semester_id = "Semester is required";
      if (!formData.subject_id) errors.subject_id = "Subject is required";
    } else if (flowType === "competitive") {
      if (!formData.exam_category_id) errors.exam_category_id = "Exam Category is required";
      if (!formData.exam_id) errors.exam_id = "Exam is required";
      if (!formData.competitive_subject_id) errors.competitive_subject_id = "Subject is required";
    }

    // Stock and Low Stock Alert validations
    if (formData.stock_quantity !== undefined && formData.stock_quantity !== "" && parseInt(formData.stock_quantity) < 0) {
      errors.stock_quantity = "Stock quantity cannot be less than 0";
    }

    if (formData.low_stock_alert !== undefined && formData.low_stock_alert !== "" && parseInt(formData.low_stock_alert) < 0) {
      errors.low_stock_alert = "Low stock alert cannot be less than 0";
    }

    // Image validation for multiple files
    if (!isEditMode && imageFiles.length === 0) {
      errors.image_url = "At least one image is required for new products";
    } else if (imageFiles.length > 10) {
      errors.image_url = "Maximum 10 images allowed";
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors).join(" | ");
      toast.error(`Validation Failed: ${errorMessages}`);
    }

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsUploading(true);

      const productData = {
        product_name: (formData.product_name || "").trim(),
        product_sku: (formData.product_sku || "").trim(),
        product_type: (flowType === "school" || flowType === "college") ? "book" : "piece",
        product_description: (formData.product_description || "").trim(),
        brand_publisher: (formData.brand_publisher || "").trim(),
        book_language: (formData.book_language || "").trim(),
        mrp: parseFloat(formData.mrp || 0),
        selling_price: parseFloat(formData.selling_price || 0),
        discount_percentage: parseFloat(formData.discount_percentage || 0),
        gst_percentage: parseFloat(formData.gst_percentage || 0),
        stock_quantity: Math.max(0, parseInt(formData.stock_quantity || 0)),
        low_stock_alert: Math.max(0, parseInt(formData.low_stock_alert || 5)),
        category_id: formData.category_id,
        sub_category_id: formData.sub_category_id,
        product_status: formData.product_status || "UNLISTED",
        board_id: formData.board_id || null,
        class_id: formData.class_id || null,
        subject_id: formData.subject_id || null,
        university_id: formData.university_id || null,
        course_id: formData.course_id || null,
        semester_id: formData.semester_id || null,
        exam_category_id: formData.exam_category_id || null,
        exam_id: formData.exam_id || null,
        competitive_subject_id: formData.competitive_subject_id || null,
        color: formData.color || null,
        size: formData.size || null,
        material: formData.material || null,
        weight: formData.weight || null,
        capacity: formData.capacity || null,
        gender: formData.gender || null,
        age_group: formData.age_group || null,
        battery_required: formData.battery_required,
        medium: formData.medium || null,
        author_name: formData.author_name || null,
        edition: formData.edition || null,
        isbn: formData.isbn || null,
        barcode: formData.barcode || null,
        preview_pdf: formData.preview_pdf || null,
        regulation_year: formData.regulation_year || null,
        vendor_id: formData.vendor_id || null,
        product_price: parseFloat(formData.product_price || 0),
        gst_amount: parseFloat(formData.gst_amount || 0),
        commission_type: formData.commission_type || "percentage",
        commission_value: parseFloat(formData.commission_value || 0),
        commission_amount: parseFloat(formData.commission_amount || 0),
      };

      if (isEditMode) {
        const result = await dispatch(
          updateProduct({
            id,
            productData,
            imageFiles,
            removedImageUrls,
          })
        ).unwrap();

        // Update existing images from the response
        const updatedProduct =
          result?.product || result?.data?.product || result;
        if (updatedProduct) {
          // Update existing images from the response
          if (
            updatedProduct.image_url &&
            Array.isArray(updatedProduct.image_url)
          ) {
            setExistingImages(updatedProduct.image_url);
          } else if (updatedProduct.image_url) {
            const parsed = parseExistingImages(updatedProduct.image_url);
            setExistingImages(parsed);
          }

          // Update currentProduct in Redux state
          dispatch(setCurrentProduct(updatedProduct));
        } else {
          // Fallback: Refresh the product data to get updated image_url
          if (id) {
            await dispatch(fetchProductById(id));
          }
        }

        // Clear new image files and removed images state after successful update
        setImageFiles([]);
        setRemovedImageUrls([]);
      } else {
        await dispatch(
          createProduct({
            productData,
            imageFiles,
          })
        ).unwrap();
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Something went wrong");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      product_name: "",
      product_sku: "",
      product_type: "piece",
      product_description: "",
      brand_publisher: "",
      book_language: "",
      mrp: 0.0,
      selling_price: 0.0,
      discount_percentage: 0.0,
      gst_percentage: 0.0,
      stock_quantity: 0,
      low_stock_alert: 5,
      category_id: "",
      sub_category_id: "",
      product_status: "UNLISTED",
      is_active: "active",
      image_url: "",
      product_price: 0.0,
      gst_amount: 0.0,
      commission_type: "percentage",
      commission_value: 0.0,
      commission_amount: 0.0,
    });
    setImageFiles([]);
    setExistingImages([]);
    setRemovedImageUrls([]);
    setValidationErrors({});
    navigate("/product");
  };

  const isSubmitting = isCreating || isUpdating || isUploading;

  console.log("formdata-->", formData);

  // Show loading state when fetching product data in edit mode
  if (isEditMode && isLoading) {
    return (
      <div className="page-container">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading product data...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Container fluid>
        <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
          <h5 className="mb-0 fw-semibold text-light">
            {isEditMode ? "Update Product" : "Create New Product"}
          </h5>
          <div>
            <Button
              variant="success"
              className="me-2 px-4"
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update"
                  : "Save"}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </div>

        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col md={12}>
                  {/* Section 1: Basic Classification */}
                  <h5 className="mb-3 border-bottom pb-2 text-primary">Basic Classification</h5>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Category <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="category_id"
                          value={formData.category_id}
                          onChange={handleInputChange}
                          isInvalid={!!validationErrors.category_id}
                          disabled={isSubmitting}
                        >
                          <option value="">Select category</option>
                          {(() => {
                            const displayCategories = [...activeCategories];
                            if (isEditMode && formData.category_id && !displayCategories.some(c => c.id === formData.category_id)) {
                              const currentCat = categories.find(c => c.id === formData.category_id);
                              if (currentCat) {
                                displayCategories.push(currentCat);
                              }
                            }
                            return displayCategories.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                                {category.status !== "Active" && " (Inactive)"}
                              </option>
                            ));
                          })()}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.category_id}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Subcategory <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="sub_category_id"
                          value={formData.sub_category_id}
                          onChange={handleInputChange}
                          isInvalid={!!validationErrors.sub_category_id}
                          disabled={!formData.category_id || isLoadingSubCats || isSubmitting}
                        >
                          <option value="">
                            {isLoadingSubCats ? "Loading..." : "Select subcategory"}
                          </option>
                          {subCategories.map((subCategory) => (
                            <option key={subCategory.id} value={subCategory.id}>
                              {subCategory.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.sub_category_id}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    {(user?.user_type === "admin" || user?.user_type === "super_admin") && (
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Vendor <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            name="vendor_id"
                            value={formData.vendor_id}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.vendor_id}
                            disabled={isSubmitting}
                          >
                            <option value="">Select Vendor</option>
                            {vendors.map((vendor) => (
                              <option key={vendor.id} value={vendor.id}>
                                {vendor.business_name || `${vendor.first_name || ""} ${vendor.last_name || ""}`.trim() || vendor.email}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.vendor_id}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>

                  {/* Section 2: Dynamic Academic Flows */}
                  {(flowType === "school" || flowType === "college" || flowType === "competitive") && (
                    <h5 className="mb-3 mt-4 border-bottom pb-2 text-primary">Academic Details</h5>
                  )}

                  {flowType === "school" && (
                    <Row className="mb-3 p-3 bg-light rounded shadow-sm mx-0">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Board <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            name="board_id"
                            value={formData.board_id}
                            onChange={(e) => handleBoardChange(e.target.value)}
                            isInvalid={!!validationErrors.board_id}
                            disabled={isSubmitting || isLoadingAcademic}
                          >
                            <option value="">Select Board</option>
                            {boards.map(b => <option key={b.id} value={b.id}>{b.board_name}</option>)}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{validationErrors.board_id}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Class <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            name="class_id"
                            value={formData.class_id}
                            onChange={(e) => handleClassChange(e.target.value)}
                            isInvalid={!!validationErrors.class_id}
                            disabled={!formData.board_id || isSubmitting || isLoadingAcademic}
                          >
                            <option value="">Select Class</option>
                            {classes.map(c => <option key={c.id} value={c.id}>{c.class_name}</option>)}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{validationErrors.class_id}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            name="subject_id"
                            value={formData.subject_id}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.subject_id}
                            disabled={!formData.class_id || isSubmitting || isLoadingAcademic}
                          >
                            <option value="">Select Subject</option>
                            {academicSubjects.map(s => <option key={s.id} value={s.id}>{s.subject_name}</option>)}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{validationErrors.subject_id}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  {flowType === "college" && (
                    <Row className="mb-3 p-3 bg-light rounded shadow-sm mx-0">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>University <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            name="university_id"
                            value={formData.university_id}
                            onChange={(e) => handleUniversityChange(e.target.value)}
                            isInvalid={!!validationErrors.university_id}
                            disabled={isSubmitting || isLoadingAcademic}
                          >
                            <option value="">Select University</option>
                            {universities.map(u => <option key={u.id} value={u.id}>{u.university_name}</option>)}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{validationErrors.university_id}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Course <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            name="course_id"
                            value={formData.course_id}
                            onChange={(e) => handleCourseChange(e.target.value)}
                            isInvalid={!!validationErrors.course_id}
                            disabled={!formData.university_id || isSubmitting || isLoadingAcademic}
                          >
                            <option value="">Select Course</option>
                            {courses.map(c => <option key={c.id} value={c.id}>{c.course_name}</option>)}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{validationErrors.course_id}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Semester <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            name="semester_id"
                            value={formData.semester_id}
                            onChange={(e) => handleSemesterChange(e.target.value)}
                            isInvalid={!!validationErrors.semester_id}
                            disabled={!formData.course_id || isSubmitting || isLoadingAcademic}
                          >
                            <option value="">Select Semester</option>
                            {semesters.map(s => <option key={s.id} value={s.id}>{s.semester_name}</option>)}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{validationErrors.semester_id}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            name="subject_id"
                            value={formData.subject_id}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.subject_id}
                            disabled={!formData.semester_id || isSubmitting || isLoadingAcademic}
                          >
                            <option value="">Select Subject</option>
                            {academicSubjects.map(s => <option key={s.id} value={s.id}>{s.subject_name}</option>)}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{validationErrors.subject_id}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  {flowType === "competitive" && (
                    <Row className="mb-3 p-3 bg-light rounded shadow-sm mx-0">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Exam Category <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            name="exam_category_id"
                            value={formData.exam_category_id}
                            onChange={(e) => handleExamCategoryChange(e.target.value)}
                            isInvalid={!!validationErrors.exam_category_id}
                            disabled={isSubmitting || isLoadingAcademic}
                          >
                            <option value="">Select Category</option>
                            {examCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{validationErrors.exam_category_id}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Exam <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            name="exam_id"
                            value={formData.exam_id}
                            onChange={(e) => handleExamChange(e.target.value)}
                            isInvalid={!!validationErrors.exam_id}
                            disabled={!formData.exam_category_id || isSubmitting || isLoadingAcademic}
                          >
                            <option value="">Select Exam</option>
                            {exams.map(e => <option key={e.id} value={e.id}>{e.exam_name}</option>)}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{validationErrors.exam_id}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Competitive Subject <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            name="competitive_subject_id"
                            value={formData.competitive_subject_id}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.competitive_subject_id}
                            disabled={!formData.exam_id || isSubmitting || isLoadingAcademic}
                          >
                            <option value="">Select Subject</option>
                            {competitiveSubjects.map(s => <option key={s.id} value={s.id}>{s.subject_name}</option>)}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{validationErrors.competitive_subject_id}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  {(flowType === "school" || flowType === "college" || flowType === "competitive") && (
                    <>
                      <h5 className="mb-3 mt-4 border-bottom pb-2 text-primary">Book Specifications</h5>
                      <Row>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Author Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="author_name"
                              value={formData.author_name}
                              onChange={handleInputChange}
                              placeholder="Enter author name"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Edition</Form.Label>
                            <Form.Control
                              type="text"
                              name="edition"
                              value={formData.edition}
                              onChange={handleInputChange}
                              placeholder="e.g. 2024 Edition"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>ISBN</Form.Label>
                            <Form.Control
                              type="text"
                              name="isbn"
                              value={formData.isbn}
                              onChange={handleInputChange}
                              placeholder="Enter ISBN"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Medium</Form.Label>
                            <Form.Select
                              name="medium"
                              value={formData.medium}
                              onChange={handleInputChange}
                              disabled={isSubmitting}
                            >
                              <option value="">Select Medium</option>
                              <option value="English">English</option>
                              <option value="Hindi">Hindi</option>
                              <option value="Bilingual">Bilingual</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        {/* <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Barcode</Form.Label>
                            <Form.Control
                              type="text"
                              name="barcode"
                              value={formData.barcode}
                              onChange={handleInputChange}
                              placeholder="Enter Barcode"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col> */}
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Regulation Year</Form.Label>
                            <Form.Control
                              type="text"
                              name="regulation_year"
                              value={formData.regulation_year}
                              onChange={handleInputChange}
                              placeholder="e.g. 2020"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col>
                        {/* <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Preview PDF URL</Form.Label>
                            <Form.Control
                              type="text"
                              name="preview_pdf"
                              value={formData.preview_pdf}
                              onChange={handleInputChange}
                              placeholder="Link to preview PDF"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col> */}
                      </Row>
                    </>
                  )}

                  {/* Section 3: Core Details */}
                  <h5 className="mb-3 mt-4 border-bottom pb-2 text-primary">Product Details</h5>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {/* {formData.product_type === "book" ? "Subject Name" : "Product Name"}{" "} */}
                          Product Name
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="product_name"
                          value={formData.product_name}
                          onChange={handleInputChange}
                          isInvalid={!!validationErrors.product_name}
                          placeholder={(flowType === "school" || flowType === "college") ? "Enter Subject name" : "Enter product name"}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.product_name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {formData.product_type === "book" ? "ISBN / SKU" : "Product SKU"}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="product_sku"
                          value={formData.product_sku}
                          onChange={handleInputChange}
                          placeholder={(flowType === "school" || flowType === "college") ? "ISBN Number" : "SKU-001"}
                          disabled={isSubmitting}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {(flowType === "school" || flowType === "college" || flowType === "competitive") ? "Publisher" : "Brand"}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="brand_publisher"
                          value={formData.brand_publisher}
                          onChange={handleInputChange}
                          placeholder={(flowType === "school" || flowType === "college") ? "Publisher name" : "Brand name"}
                          disabled={isSubmitting}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {flowType === "normal" && (
                    <>
                      <h5 className="mb-3 mt-4 border-bottom pb-2 text-primary">Attributes (Optional)</h5>
                      <Row>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Color</Form.Label>
                            <Form.Control
                              type="text"
                              name="color"
                              value={formData.color}
                              onChange={handleInputChange}
                              placeholder="e.g. Red, Blue"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Size</Form.Label>
                            <Form.Control
                              type="text"
                              name="size"
                              value={formData.size}
                              onChange={handleInputChange}
                              placeholder="e.g. XL, 10-inch"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Material</Form.Label>
                            <Form.Control
                              type="text"
                              name="material"
                              value={formData.material}
                              onChange={handleInputChange}
                              placeholder="e.g. Plastic, Wood"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Weight</Form.Label>
                            <Form.Control
                              type="text"
                              name="weight"
                              value={formData.weight}
                              onChange={handleInputChange}
                              placeholder="e.g. 500g"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Capacity</Form.Label>
                            <Form.Control
                              type="text"
                              name="capacity"
                              value={formData.capacity}
                              onChange={handleInputChange}
                              placeholder="e.g. 1L, 50 Sheets"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Gender</Form.Label>
                            <Form.Select
                              name="gender"
                              value={formData.gender}
                              onChange={handleInputChange}
                              disabled={isSubmitting}
                            >
                              <option value="">Select Gender</option>
                              <option value="Unisex">Unisex</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Age Group</Form.Label>
                            <Form.Control
                              type="text"
                              name="age_group"
                              value={formData.age_group}
                              onChange={handleInputChange}
                              placeholder="e.g. 3-5 years"
                              disabled={isSubmitting}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Check
                              type="checkbox"
                              label="Battery Required"
                              name="battery_required"
                              checked={formData.battery_required}
                              onChange={(e) => setFormData(prev => ({ ...prev, battery_required: e.target.checked }))}
                              disabled={isSubmitting}
                              style={{ marginTop: '35px' }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}

                  {/* Section 4: Inventory & Status */}
                  <h5 className="mb-3 mt-4 border-bottom pb-2 text-primary">Inventory & Status</h5>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Stock Quantity</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          name="stock_quantity"
                          value={formData.stock_quantity}
                          onChange={handleInputChange}
                          onKeyDown={(e) => {
                            if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault();
                          }}
                          isInvalid={!!validationErrors.stock_quantity}
                          placeholder="0"
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.stock_quantity}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Low Stock Alert</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          name="low_stock_alert"
                          value={formData.low_stock_alert}
                          onChange={handleInputChange}
                          onKeyDown={(e) => {
                            if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault();
                          }}
                          isInvalid={!!validationErrors.low_stock_alert}
                          placeholder="5"
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.low_stock_alert}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Status <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="product_status"
                          value={formData.product_status}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        >
                          <option value="UNLISTED">UNLISTED</option>
                          <option value="PUBLISHED">PUBLISHED</option>
                          <option value="HOLD">HOLD</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Section 5: Pricing & Tax */}
                  <h5 className="mb-3 mt-4 border-bottom pb-2 text-primary">Pricing & Tax</h5>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ minHeight: '40px', display: 'flex', alignItems: 'flex-end', marginBottom: '8px' }}>
                          MRP <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          name="mrp"
                          value={formData.mrp}
                          onChange={handleInputChange}
                          isInvalid={!!validationErrors.mrp}
                          placeholder="0.00"
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.mrp}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ minHeight: '40px', display: 'flex', alignItems: 'flex-end', marginBottom: '8px' }}>
                          Product Price <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          name="product_price"
                          value={formData.product_price}
                          onChange={handleInputChange}
                          isInvalid={!!validationErrors.product_price}
                          placeholder="0.00"
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.product_price}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ minHeight: '40px', display: 'flex', alignItems: 'flex-end', marginBottom: '8px' }}>GST (%)</Form.Label>
                        <Form.Control
                          type="number"
                          name="gst_percentage"
                          value={formData.gst_percentage}
                          disabled
                          placeholder="0"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ minHeight: '40px', display: 'flex', alignItems: 'flex-end', marginBottom: '8px' }}>GST Amount</Form.Label>
                        <Form.Control
                          type="number"
                          name="gst_amount"
                          value={formData.gst_amount}
                          disabled
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ minHeight: '40px', display: 'flex', alignItems: 'flex-end', marginBottom: '8px' }}>Vendor Price (Inc. GST)</Form.Label>
                        <Form.Control
                          type="number"
                          value={(parseFloat(formData.product_price || 0) + parseFloat(formData.gst_amount || 0)).toFixed(2)}
                          disabled
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ minHeight: '40px', display: 'flex', alignItems: 'flex-end', marginBottom: '8px' }}>
                          Platform Fee ({formData.commission_type === 'fixed' ? 'Fixed ₹' : ''}{formData.commission_value}{formData.commission_type === 'percentage' ? '%' : ''})
                        </Form.Label>
                        <Form.Control
                          type="number"
                          value={formData.commission_amount}
                          disabled
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ minHeight: '40px', display: 'flex', alignItems: 'flex-end', marginBottom: '8px' }}>
                          Selling Price
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="selling_price"
                          value={formData.selling_price}
                          disabled
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ minHeight: '40px', display: 'flex', alignItems: 'flex-end', marginBottom: '8px' }}>Discount Percentage (%)</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          name="discount_percentage"
                          value={formData.discount_percentage}
                          onChange={handleInputChange}
                          placeholder="0.00"
                          disabled={isSubmitting || !formData.mrp || parseFloat(formData.mrp) <= 0}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  {/* <Row className="mb-3">
                    <Col md={12}>
                      <div className="p-3 bg-light rounded border d-flex align-items-center">
                        <i className="fa-solid fa-calculator text-primary me-3 fs-4"></i>
                        <div>
                          <strong className="text-primary d-block mb-1">Pricing Breakdown:</strong>
                          <span className="text-muted small">
                            Formula: (Product Price: <strong>₹{formData.product_price || '0.00'}</strong> + GST: <strong>{formData.gst_percentage || 0}%</strong>) 
                            + Platform Fee: <strong>₹{formData.commission_amount || '0.00'}</strong> 
                            = Selling Price: <strong className="text-success">₹{formData.selling_price || '0.00'}</strong>
                          </span>
                        </div>
                      </div>
                    </Col>
                  </Row> */}

                  {(flowType === "school" || flowType === "college") && (
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Book Language</Form.Label>
                          <Form.Select
                            name="book_language"
                            value={formData.book_language}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                          >
                            <option value="English">English</option>
                            <option value="Hindi">Hindi</option>
                            <option value="Rajasthani">Rajasthani</option>
                            <option value="Sanskrit">Sanskrit</option>
                            <option value="Marwari">Marwari</option>
                            <option value="Mewari">Mewari</option>
                            <option value="Hadoti">Hadoti</option>
                            <option value="Dhundhari">Dhundhari</option>
                            <option value="Urdu">Urdu</option>
                            <option value="Sindhi">Sindhi</option>
                            <option value="Punjabi">Punjabi</option>
                            <option value="Gujarati">Gujarati</option>
                            <option value="Braj Bhasha">Braj Bhasha</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  {/* Section 6: Images & Description */}
                  <h5 className="mb-3 mt-4 border-bottom pb-2 text-primary">Media & Description</h5>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Product Images{" "}
                          {!isEditMode && (
                            <span className="text-danger">*</span>
                          )}
                        </Form.Label>
                        <div className="mb-2 p-2 rounded bg-danger-soft border border-danger">
                          <p className="text-danger fw-bold mb-0 small">
                            <i className="fa-solid fa-triangle-exclamation me-2"></i>
                            IMPORTANT NOTE: All images must be exactly 2200 x 2000 pixels. Files with other dimensions will be rejected.
                          </p>
                        </div>
                        <Form.Control
                          id="file-uplad"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageSelect}
                          disabled={isSubmitting}
                          isInvalid={!!validationErrors.image_url}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.image_url}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                          Select multiple images (max 10, 5MB each)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Description and Image Preview */}
                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>Product Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="product_description"
                          value={formData.product_description}
                          onChange={handleInputChange}
                          placeholder="Enter product description"
                          disabled={isSubmitting}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      {/* Multiple image preview section */}
                      {(imageFiles.length > 0 ||
                        (isEditMode && existingImages.length > 0)) && (
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <strong>
                                Images (
                                {imageFiles.length + existingImages.length})
                              </strong>
                            </Form.Label>
                            <div className="row g-2">
                              {/* Show new selected files */}
                              {imageFiles.map((file, index) => (
                                <div
                                  key={`new-${index}`}
                                  className="col-2 col-md-2"
                                >
                                  <div
                                    className="border rounded p-2 image-card-container"
                                    style={{
                                      height: "150px",
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <img
                                      src={URL.createObjectURL(file)}
                                      alt={`Preview ${index + 1}`}
                                      style={{
                                        width: "100%",
                                        height: "50px",
                                        objectFit: "cover",
                                        borderRadius: "4px",
                                      }}
                                    />
                                    <div
                                      className="mt-1 flex-grow-1"
                                      style={{ overflow: "hidden" }}
                                    >
                                      <small
                                        className="text-muted d-block"
                                        style={{
                                          fontSize: "0.7rem",
                                          display: "-webkit-box",
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: "vertical",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          lineHeight: "1.2",
                                          height: "2.4em",
                                        }}
                                      >
                                        {file.name}
                                      </small>
                                    </div>
                                    <Button
                                      variant="outline-danger-custom"
                                      size="sm"
                                      className="mt-1"
                                      onClick={() => handleRemoveImage(index)}
                                      disabled={isSubmitting}
                                      style={{
                                        fontSize: "0.7rem",
                                        padding: "0.2rem 0.4rem",
                                        width: "fit-content",
                                      }}
                                    >
                                      <FaRegTrashAlt />
                                    </Button>
                                  </div>
                                </div>
                              ))}

                              {/* Show existing images in edit mode */}
                              {isEditMode &&
                                existingImages.map((imageUrl, index) => (
                                  <div
                                    key={`existing-${index}`}
                                    className="col-2 col-md-2"
                                  >
                                    <div
                                      className="border rounded p-2 image-card-container"
                                      style={{
                                        height: "150px",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <img
                                        src={imageUrl}
                                        alt={`Existing ${index + 1}`}
                                        style={{
                                          width: "100%",
                                          height: "50px",
                                          objectFit: "cover",
                                          borderRadius: "4px",
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                        }}
                                      />
                                      <div
                                        className="mt-1 flex-grow-1"
                                        style={{ overflow: "hidden" }}
                                      >
                                        <small
                                          className="text-muted d-block"
                                          style={{
                                            fontSize: "0.7rem",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            lineHeight: "1.2",
                                            height: "2.4em",
                                          }}
                                        >
                                          Existing Image {index + 1}
                                        </small>
                                      </div>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="mt-1"
                                        onClick={() =>
                                          handleRemoveExistingImage(index)
                                        }
                                        disabled={isSubmitting}
                                        style={{
                                          fontSize: "0.7rem",
                                          padding: "0.2rem 0.4rem",
                                          width: "fit-content",
                                        }}
                                      >
                                        <FaRegTrashAlt />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </Form.Group>
                        )}
                    </Col>
                  </Row>


                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default ProductForm;
